import {
  Auth0TokenClaims,
  CognitoTokenClaims,
  NormalizedTokenClaims,
} from './types';

/**
 * Detect if token claims are from Auth0
 */
export const isAuth0Token = (
  claims: Auth0TokenClaims | CognitoTokenClaims
): claims is Auth0TokenClaims => {
  return (
    'https://mypages/email' in claims ||
    'https://mypages/ssn' in claims ||
    'https://mypages/market' in claims ||
    'https://onboarding/clientId' in claims
  );
};

/**
 * Detect if token claims are from Cognito
 */
export const isCognitoToken = (
  claims: Auth0TokenClaims | CognitoTokenClaims
): claims is CognitoTokenClaims => {
  return (
    'custom:ssn' in claims ||
    'custom:market' in claims ||
    'custom:client_id' in claims ||
    'token_use' in claims
  );
};

/**
 * Normalize Auth0 token claims to common format
 */
export const normalizeAuth0Claims = (
  claims: Auth0TokenClaims
): NormalizedTokenClaims => {
  return {
    email: claims['https://mypages/email'],
    ssn: claims['https://mypages/ssn'],
    market: claims['https://mypages/market'],
    clientId: claims['https://onboarding/clientId'],
    userCode: claims['https://onboarding/userCode'],
    dateOfBirth: claims['https://onboarding/dateOfBirth'],
    sub: claims.sub,
    aud: claims.aud,
    exp: claims.exp,
    iat: claims.iat,
    iss: claims.iss,
  };
};

/**
 * Normalize Cognito token claims to common format
 */
export const normalizeCognitoToken = (
  claims: CognitoTokenClaims
): NormalizedTokenClaims => {
  return {
    email: claims.email,
    ssn: claims['custom:ssn'],
    market: claims['custom:market'],
    clientId: claims['custom:client_id'],
    userCode: claims['custom:user_code'],
    dateOfBirth: claims['custom:date_of_birth'],
    sub: claims.sub,
    aud: claims.aud,
    exp: claims.exp,
    iat: claims.iat,
    iss: claims.iss,
  };
};

/**
 * Normalize token claims from either Auth0 or Cognito to a common format
 * Automatically detects the provider and applies the appropriate normalization
 */
export const normalizeTokenClaims = (
  claims: Auth0TokenClaims | CognitoTokenClaims | Record<string, unknown>
): NormalizedTokenClaims => {
  // If already normalized, return as-is
  const claimsRecord = claims as Record<string, unknown>;
  if (
    claimsRecord.email &&
    !claimsRecord['https://mypages/email'] &&
    !claimsRecord['custom:ssn']
  ) {
    return claims as NormalizedTokenClaims;
  }

  // Detect and normalize based on provider
  if (isAuth0Token(claims)) {
    return normalizeAuth0Claims(claims);
  }

  if (isCognitoToken(claims)) {
    return normalizeCognitoToken(claims);
  }

  // Fallback: return claims as-is if we can't detect the provider
  return claims as NormalizedTokenClaims;
};

/**
 * Parse a JWT token and return its claims
 */
export const parseJwt = (token: string): any => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error parsing JWT:', error);
    return {};
  }
};

/**
 * Check if a JWT token is expired
 */
export const isTokenExpired = (token: string): boolean => {
  try {
    const claims = parseJwt(token);
    if (!claims.exp) {
      return true;
    }
    const now = Date.now() / 1000;
    return claims.exp < now;
  } catch {
    return true;
  }
};

/**
 * Get the provider type from a JWT token
 */
export const getProviderFromToken = (
  token: string
): 'auth0' | 'cognito' | 'unknown' => {
  try {
    const claims = parseJwt(token);
    if (isAuth0Token(claims)) {
      return 'auth0';
    }
    if (isCognitoToken(claims)) {
      return 'cognito';
    }
    return 'unknown';
  } catch {
    return 'unknown';
  }
};
